import type { StrikeType } from 'models/market';

import { unitStringToNumber } from './number';

export interface NumericTarget {
    upperBound: number | null;
    lowerBound: number | null;
}

/** Inputs `numericTarget` resolution needs, kept narrow so the same logic can
 * run in domain code (BinaryMarket) and in server-only contexts (preview
 * route, OG image generator) without dragging the store layer along. */
export interface NumericTargetInput {
    /** The market's `yes_sub_title` label. */
    yesSubtitle: string;
    strikeType: StrikeType;
    rulebookVariables: Record<string, string>;
    /** Top-level `floor_strike` from the API; missing on pre-Feb-2025 series. */
    floorStrike: number | undefined;
    capStrike: number | undefined;
}

/** Valid `rulebook_variables` separator *values*. Matched by value, not key —
 * the key drifts across templates (35+ variants in production: `Above/below/
 * between`, `Greater than/Less than/Between`, typos like `bewteen`/`bellow`),
 * but the value space is small. The first seven cover >99% of structured
 * markets after `.toLowerCase()`; the remaining `or higher`/`or above`/etc.
 * are a defensive net for structured markets that happen to use those
 * phrasings (most resolve in tier 1 via `strike_type`). */
const SEPARATOR_VALUES = new Set([
    'above',
    'below',
    'between',
    'exactly',
    'at least',
    'more than',
    'less than',
    'or higher',
    'or above',
    'or lower',
    'or below',
    // Legacy template values — rare, retained so older markets still parse.
    'over',
    'or more',
    'equal to',
    'more',
]);

const unitOrDollarStringToNumber = (unit: string): number => {
    const trimmedUnit = unit.trim();
    if (trimmedUnit.charAt(0) === '$') return unitStringToNumber(trimmedUnit.slice(1));
    return unitStringToNumber(trimmedUnit);
};

const SUBTITLE_DOLLAR_VALUES = /\$([\d,]+\.\d+)/g;

/**
 * Decimal places in a number, including values JavaScript prints in exponent form.
 *
 * `(1e-7).toString()` is `"1e-7"`, so splitting on `.` reports zero decimals and the recovery below would
 * compare at scale 1 and silently do nothing. Small crypto strikes reach this resolver, so the exponent
 * has to be folded into the count.
 */
const decimalPlaces = (value: number): number => {
    const str = value.toString();
    const exponent = str.match(/e-(\d+)$/);
    const mantissa = exponent ? str.slice(0, exponent.index) : str;
    const fraction = mantissa.split('.')[1] ?? '';
    return fraction.length + (exponent ? Number(exponent[1]) : 0);
};

/**
 * Restores strike digits that the JSON number dropped.
 *
 * `floor_strike` and `cap_strike` arrive as JSON numbers and can carry fewer significant digits than the
 * strike itself. Callers that render the strike to a width read from the subtitle then pad the shortfall
 * with zeros, which invents a digit rather than restoring one: a strike of 0.0706867 arriving as 0.070686
 * rendered as $0.0706860, so the knockout header disagreed with the target price shown beneath it.
 *
 * A subtitle price is used only when it collapses to this strike at the strike's own precision, and only
 * when it is the sole price that does. Requiring uniqueness is what keeps a `between` market honest: given
 * strikes 1 and 2 and "Between $1.99 and $2.99", both prices collapse to 2 (one by truncation, one by
 * rounding), so the cap is left alone rather than taking the floor's price and flattening the range.
 *
 * The result is that this only ever sharpens a value. It never substitutes a different one.
 */
const withSubtitlePrecision = (strike: number, subtitle: string): number => {
    if (!subtitle) return strike;

    const strikeDecimals = decimalPlaces(strike);
    const scale = 10 ** strikeDecimals;
    const matches = new Set<number>();

    for (const [, raw] of subtitle.matchAll(SUBTITLE_DOLLAR_VALUES)) {
        const parsed = Number(raw.replace(/,/g, ''));
        if (!Number.isFinite(parsed) || decimalPlaces(parsed) <= strikeDecimals) continue;

        // The number arrives truncated rather than rounded (0.0706867 -> 0.070686), but accept either so
        // the check does not depend on which the producer does.
        const truncated = Math.trunc(parsed * scale + Number.EPSILON) / scale;
        const rounded = Number(parsed.toFixed(strikeDecimals));
        if (truncated === strike || rounded === strike) matches.add(parsed);
    }

    return matches.size === 1 ? [...matches][0] : strike;
}; /** Tiered resolver for a market's numeric bounds:
 *  1. Typed `strike_type` enum (`greater`/`less`/`between`/...) — most numeric
 *     markets, no text parsing.
 *  2. `rulebook_variables` separator value — for `strike_type === "structured"`
 *     markets (sports props, artist streams, etc.) where comparison semantics
 *     live in a freeform rulebook variable.
 *  3. Subtitle text parsing — legacy fallback for pre-2025 series whose
 *     markets don't surface the structured fields. */
export function computeNumericTarget(input: NumericTargetInput): NumericTarget | null {
    const { yesSubtitle: subtitle, strikeType, rulebookVariables, floorStrike, capStrike } = input;

    // (1) Typed `strike_type` enum (preferred). The backend's
    // `MarketStrikeType` (svc-market-metadata `unimodel/market_strike_metadata.go`,
    // shipped Feb 2025 in #7013) is the source of truth for comparison
    // semantics. `numericTarget` doesn't model strict vs. inclusive (callers
    // read `strikeType` for that), so `greater` and `greater_or_equal`
    // collapse to the same bound shape.
    switch (strikeType) {
        case 'greater':
        case 'greater_or_equal':
            if (floorStrike !== undefined)
                return { lowerBound: withSubtitlePrecision(floorStrike, subtitle), upperBound: null };
            break;
        case 'less':
        case 'less_or_equal':
            if (capStrike !== undefined)
                return { lowerBound: null, upperBound: withSubtitlePrecision(capStrike, subtitle) };
            break;
        case 'between':
            if (floorStrike !== undefined && capStrike !== undefined)
                return {
                    lowerBound: withSubtitlePrecision(floorStrike, subtitle),
                    upperBound: withSubtitlePrecision(capStrike, subtitle),
                };
            break;
    }

    // (2) Structured-strike separator-value fallback. `strike_type ===
    // "structured"` markets (e.g. artist streams with `custom_strike`) carry
    // their comparison semantics in `rulebook_variables` instead of the typed
    // enum. Real separator keys list their valid values (`Above/below/between`,
    // `Above/below/exactly/at least/between`, …), so requiring the key to
    // contain the matched value as a substring keeps the check tight without
    // depending on the drifty key naming.
    const separatorValue = Object.entries(rulebookVariables)
        .find(([key, value]) => {
            const v = value.toLowerCase();
            return SEPARATOR_VALUES.has(v) && key.toLowerCase().includes(v);
        })?.[1]
        .toLowerCase();
    if (separatorValue !== undefined) {
        switch (separatorValue) {
            case 'above':
            case 'more than':
            case 'at least':
            case 'or higher':
            case 'or above':
            case 'over': // legacy
            case 'or more': // legacy
            case 'more': // legacy
                if (floorStrike !== undefined) return { lowerBound: floorStrike, upperBound: null };
                break;
            case 'below':
            case 'less than':
            case 'or lower':
            case 'or below':
                if (capStrike !== undefined) return { lowerBound: null, upperBound: capStrike };
                break;
            case 'between':
                if (floorStrike !== undefined && capStrike !== undefined)
                    return { lowerBound: floorStrike, upperBound: capStrike };
                break;
            case 'exactly':
            case 'equal to': // legacy
                if (floorStrike !== undefined) return { lowerBound: floorStrike, upperBound: floorStrike };
                break;
        }
    }

    // (3) Subtitle-text fallback. The original parser (Jan 2022) predates both
    // `strike_type` (Feb 2025, #7013) and `rulebook_variables` (Jan 2025,
    // #6807) — kept around so pre-2025 series whose markets don't surface the
    // structured fields still produce a NumericTarget.

    if (!subtitle) {
        return null;
    }

    // Match prefixes/suffixes case-insensitively. MarketOps templates have
    // historically emitted both casings ("Above 938M" and "above 938M"), so we
    // normalize. Slice from the original `subtitle` to preserve unit casing —
    // `unitOrDollarStringToNumber` handles "938M" but lowercasing would also
    // flatten things like "$1.2K".
    const subLower = subtitle.toLowerCase();

    if (subLower[0] === '>') {
        const target = unitOrDollarStringToNumber(subtitle.slice(1));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(-9) === 'or higher' || subLower.slice(-8) === 'or above') {
        const target = unitOrDollarStringToNumber(subtitle.slice(0, -9));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(0, 6) === 'above ') {
        const target = unitOrDollarStringToNumber(subtitle.slice(6));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(0, 14) === 'price to beat:') {
        const target = unitOrDollarStringToNumber(subtitle.slice(14));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(0, 13) === 'target price:') {
        const target = unitOrDollarStringToNumber(subtitle.slice(13));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(0, 10) === 'more than ') {
        const target = unitOrDollarStringToNumber(subtitle.slice(10));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower.slice(0, 9) === 'at least ') {
        const target = unitOrDollarStringToNumber(subtitle.slice(9));
        if (!isNaN(target)) {
            return { lowerBound: target, upperBound: null };
        }
    } else if (subLower[0] === '<') {
        const target = unitOrDollarStringToNumber(subtitle.slice(1));
        if (!isNaN(target)) {
            return { lowerBound: null, upperBound: target };
        }
    } else if (subLower.slice(0, 6) === 'below ') {
        const target = unitOrDollarStringToNumber(subtitle.slice(6));
        if (!isNaN(target)) {
            return { lowerBound: null, upperBound: target };
        }
    } else if (subLower.slice(-8) === 'or lower' || subLower.slice(-8) === 'or below') {
        const target = unitOrDollarStringToNumber(subtitle.slice(0, -8));
        if (!isNaN(target)) {
            return { lowerBound: null, upperBound: target };
        }
    } else if (subLower.includes('between') && subLower.includes('-')) {
        const workingSub = subtitle.split(/between /i)[1];
        const targets = workingSub.split('-').map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return { lowerBound: targets[0], upperBound: targets[1] };
        }
    } else if (subtitle.includes('-')) {
        const targets = subtitle.split('-').map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return { lowerBound: targets[0], upperBound: targets[1] };
        }
    } else if (subLower.includes(' to ')) {
        const targets = subtitle.split(/ to /i).map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return { lowerBound: targets[0], upperBound: targets[1] };
        }
    }

    // Check for an exact target.
    const target = unitOrDollarStringToNumber(subtitle);
    if (!isNaN(target)) {
        return { lowerBound: target, upperBound: target };
    }

    return null;
}
